import { InputAdornment } from "@mui/material";
import { IconButton } from "@mui/material";
import { CloudSearchConfig } from "./CloudSearchConfig";
import { OpenSearchConfig } from "./OpenSearchConfig";
import { entryType, OPENSEARCH, SERIAL_NUMBER } from "../../constants";
import { getSearchEngineFieldType, getFieldValue } from "@digitallab/grid-common-components";

export const hasFormikObjectError = (formik, fieldName) => {
  return (
    (formik.touched[fieldName]?.value || formik.touched[fieldName] === true) &&
    Boolean(formik.errors[fieldName]?.value ?? formik.errors[fieldName])
  );
};
export const getFormikObjectError = (formik, fieldName) => {
  return (
    (formik.touched[fieldName]?.value || formik.touched[fieldName] === true) &&
    (formik.errors[fieldName]?.value ?? formik.errors[fieldName])
  );
};

const commonPropsForInputs =
  ({
    fieldKeyFn = (key) => `${key}.value`,
    valueFn = (formik, key) => formik.values?.[key]?.value,
    errorFn = (formik, key) => hasFormikObjectError(formik, key),
    helperTextFn = (formik, key) => getFormikObjectError(formik, key)
  } = {}) =>
    (classes, formik, key, dataTestIdKey, dataTestIdForFormKey, isEdit = false) => ({
      InputProps: valueFn(formik, key)?.length > 0 &&
        !isEdit && {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              classes={{
                root: classes.clearInputButton
              }}
              data-testid={`${dataTestIdForFormKey}-clear-button-${dataTestIdKey}-input`}
              edge="end"
              onClick={() => formik.setFieldValue(fieldKeyFn(key), "", true)}
            >
              <i className="one-icons-outlined">clear</i>
            </IconButton>
          </InputAdornment>
        )
      },
      error: errorFn(formik, key),
      helperText: helperTextFn(formik, key),
      FormHelperTextProps: {
        "data-testid": `${dataTestIdForFormKey}-helper-text-${dataTestIdKey}-input`
      }
    });

export const commonPropsForInputsWithValue = commonPropsForInputs();
export const commonPropsForInputsWithoutValue = commonPropsForInputs({
  fieldKeyFn: (key) => key,
  valueFn: (formik, key) => formik.values?.[key],
  errorFn: (formik, key) => formik.touched[key] && Boolean(formik.errors[key]),
  helperTextFn: (formik, key) => formik.touched[key] && formik.errors[key]
});

export const removeSpaceInString = (string) => {
  return string ? string.replace(/ /g, "") : string;
};

const checkShouldKeepFetching = (items, totalCount) => {
  return items.length < totalCount;
};

const fetchOne = async ({ variables, items }) => {
  const result = await (OPENSEARCH_CONFIGURATION ? OpenSearchConfig(variables) : CloudSearchConfig(variables));
  items = [...items, ...result?.data];
  let totalCount = result?.count;
  const shouldKeepFetching = checkShouldKeepFetching(items, totalCount);

  return { items, shouldKeepFetching, totalCount };
};

export const getAllCloudSearchData = async ({ requestObj = {}, searchEngineMapper }) => {
  try {
    let items = [];
    let shouldKeepFetching = true;
    do {
      ({ items, shouldKeepFetching } = await fetchOne({
        variables: {
          negatefield: requestObj?.negatefield || "",
          sort: requestObj?.sort || "asc",
          sortBy: requestObj?.sortBy || getSortField(SERIAL_NUMBER, searchEngineMapper),
          start: items?.length,
          size: requestObj?.size || 10000,
          searchValue:
            requestObj?.searchValue ||
            `-entry_type:'${entryType?.clusterSubequipment}' AND entry_type:'${entryType?.cluster}' AND cluster_id:*`
        },
        items
      }));
    } while (shouldKeepFetching);
    return {
      items,
      error: null
    };
  } catch (err) {
    return { items: [], error: err };
  }
};

export const getMegaClusterIds = async (searchEngineMapper) => {
  const requestObj = {
    sort: "asc",
    sortBy: getSortField(SERIAL_NUMBER, searchEngineMapper),
    start: 0,
    size: 10000,
    searchValue: `-entry_type:'${entryType?.clusterSubequipment}' AND entry_type:'${entryType?.cluster}' AND cluster_id:*`
  };
  let result = await getAllCloudSearchData({ requestObj, searchEngineMapper });
  let mclusterIds = [];
  if (result?.items?.length > 0) {
    mclusterIds = result?.items?.map((item) => item?.cluster_id && getFieldValue(item, "cluster_id"));
  }
  return mclusterIds;
};

export const SEARCH_ENGINE_FIELD_TYPES = {
  LITERAL: 'literal',
  TEXT: 'text',
  DATE: 'date',
  INT: 'int',
  DOUBLE: 'double',
  TEXT_ARRAY: 'text-array'
}

export const OPENSEARCH_CONFIGURATION = process.env.REACT_APP_SEARCH_ENGINE?.toUpperCase() === OPENSEARCH;


export const getSortField = (fieldName, searchEngineMapper) => {
  if (!fieldName) { return fieldName; }
  if (!OPENSEARCH_CONFIGURATION) {
    return fieldName;
  }
  const fieldType = getSearchEngineFieldType(searchEngineMapper, fieldName, SEARCH_ENGINE_FIELD_TYPES.TEXT);
  if (fieldType === SEARCH_ENGINE_FIELD_TYPES.TEXT || fieldType === SEARCH_ENGINE_FIELD_TYPES.LITERAL) {
    return `${fieldName}.keyword`;
  }
  return fieldName;
};

export const replaceSpecialCharsWithSpace = (value) => {
  if (!value || typeof value !== 'string') {
    return value;
  }
  return value.replace(/[!@#$%^&*()+=|[\]{};:|,<>?~ \-"/]/g, " ");
};
